import { Link } from 'react-router-dom';
import {
  Badge,
  Delta,
  FloorRail,
  Price,
  Progress,
  Sparkline,
  TD,
  TH,
  TokenMark,
  TR,
} from '@/components/ui';
import { useMarket } from '@/providers/market';
import { compact, eth, share, weiToNumber } from '@/lib/format';
import type { DemoToken } from '@/lib/demo/types';
import type { SortKey } from './FilterBar';

const clamp01 = (n: number) => (Number.isFinite(n) ? Math.max(0, Math.min(1, n)) : 0);

/** Column → sort key map, so the table header and the FilterBar stay in one state. */
export function TokenRowHeader({
  sort,
  onSort,
}: {
  sort: SortKey;
  onSort: (sort: SortKey) => void;
}) {
  const changeActive = sort === 'gainers' || sort === 'losers';
  return (
    <TR>
      <TH align="right">#</TH>
      <TH>Token</TH>
      <TH align="right">Price</TH>
      <TH align="right">1h</TH>
      <TH
        align="right"
        sortable
        active={changeActive}
        dir={sort === 'losers' ? 'asc' : 'desc'}
        onSort={() => onSort(sort === 'gainers' ? 'losers' : 'gainers')}
      >
        24h
      </TH>
      <TH
        align="right"
        sortable
        active={sort === 'volume'}
        dir="desc"
        onSort={() => onSort('volume')}
      >
        24h volume
      </TH>
      <TH align="right" sortable active={sort === 'mcap'} dir="desc" onSort={() => onSort('mcap')}>
        Market cap
      </TH>
      <TH align="right" sortable active={sort === 'floor'} dir="desc" onSort={() => onSort('floor')}>
        Floor
      </TH>
      <TH align="right">Holders</TH>
      <TH
        align="right"
        sortable
        active={sort === 'progress'}
        dir="desc"
        onSort={() => onSort('progress')}
      >
        Graduation
      </TH>
      <TH align="right">24h</TH>
    </TR>
  );
}

export function TokenRow({ token, rank }: { token: DemoToken; rank: number }) {
  const { getSparkline } = useMarket();

  const spot = weiToNumber(token.spotPrice);
  const ratio = clamp01(token.floorRatio);
  const progress = clamp01(token.graduationProgress);

  return (
    <TR interactive className="relative">
      <TD align="right" className="text-2xs text-subtle">
        {rank}
      </TD>

      <TD>
        {/* One link per row: the pseudo-element stretches it over every cell. */}
        <Link
          to={`/token/${token.address}`}
          className="flex min-w-0 items-center gap-2.5 after:absolute after:inset-0 after:content-['']"
        >
          <TokenMark symbol={token.symbol} hue={token.hue} glyph={token.glyph} size="sm" />
          <span className="min-w-0">
            <span className="block truncate font-mono text-xs font-semibold uppercase text-fg">
              {token.symbol}
            </span>
            <span className="block max-w-[11rem] truncate text-2xs text-muted">{token.name}</span>
          </span>
        </Link>
      </TD>

      <TD align="right">
        <Price value={spot} />
      </TD>

      <TD align="right">
        <Delta value={token.change1h} className="justify-end text-2xs" />
      </TD>

      <TD align="right">
        <Delta value={token.change24h} className="justify-end text-2xs" />
      </TD>

      <TD align="right">{eth(token.volume24hEth, { unit: false })}</TD>

      <TD align="right">{eth(token.marketCapEth, { unit: false })}</TD>

      <TD align="right">
        <div className="ml-auto w-20">
          <div className="text-2xs text-floor">{share(ratio)}</div>
          <FloorRail className="mt-1" floorRatio={ratio} height={4} />
        </div>
      </TD>

      <TD align="right">{compact(token.holderCount, 0)}</TD>

      <TD align="right">
        {token.graduated ? (
          <Badge variant="up" size="sm">
            Graduated
          </Badge>
        ) : (
          <div className="ml-auto w-24">
            <Progress value={progress} tone="bull" size="sm" showValue />
          </div>
        )}
      </TD>

      <TD align="right">
        {/* Sparkline stretches to its container, so the 88px box is the geometry. */}
        <div className="ml-auto w-[88px]">
          <Sparkline
            data={getSparkline(token.address)}
            floorRatio={ratio}
            tone="auto"
            width={88}
            height={28}
          />
        </div>
      </TD>
    </TR>
  );
}
